import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { IEmojiData } from 'emoji-picker-react';
import * as React from 'react';
import { toast } from 'sonner';
import { SocialMediaPostViewModel } from '../../../../extViewmodels';
import { useFacebookPageAutoCompleteViewModel } from '../../../../queries/Social/useFacebookPageAutoCompleteViewModel';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	getFacebookMentionRanges,
	getFacebookPageLocationLabel,
	getInstagramCaptionCounts,
	getInstagramHandles,
	hasInstagramPageMentions,
	getMentionSegments,
	INSTAGRAM_CAPTION_LIMITS,
} from '../../../../models/utils/socialUtils';
import { ImpersonationContextViewModel } from '../../../../viewmodels/AdminViewModels';
import { useBrandingSettingsQuery } from '../../../../queries';
import { hasBrandingSettings } from '../../../../utils/hasBrandingSettings';
import { AiButton } from '../../../components-v2/AiButton';
import { Button } from '../../../components-v2/Button';
import { Pill } from '../../../components-v2/Pill';
import { TextArea } from '../../../components-v2/TextArea';
import { useEditSocialMediaPostContext } from '../../../containers/socialMedia/EditSocialMediaPost/context';
import { AutoCompleteDropdown } from '../../autocomplete/AutoCompleteDropdown';
import { EmojiPickerButton } from '../../texting/EmojiPickerButton';
import { InstagramHandlesWarning } from '../PlatformCustomization/InstagramHandlesWarning';
import { PlatformLimits } from '../PlatformCustomization/PlatformLimits';
import { PlatformValidationErrors } from '../PlatformCustomization/PlatformValidationErrors';
import { PlatformVersionBar } from '../PlatformCustomization/PlatformVersionBar';
import { isFacebookTargeted } from '../../../../models/utils/platformCustomizations';
import { usePlatformCustomization } from '../PlatformCustomization/usePlatformCustomization';
import { SocialMediaImageSearchAndDisplay } from '../SocialMediaImageSearchAndDisplay';
import { getRebrandBlockedReason } from '../SocialMediaImageSearchAndDisplay/mediaRules';
import { styleSheet } from './styles';
import { type IBrandingState, getPostBrandingVersions, useBrandPostMedia } from './useBrandPostMedia';
import { useBrandingTabIndicator } from './useBrandingTabIndicator';

const CONTENT_TEXT_AREA_ID = 'social-media-content';
const VERSION_PANEL_ID = 'social-media-version';

interface IProps {
	/** Opt-in for the per-platform version controls; off for surfaces that author a single body. */
	allowPlatformCustomization?: boolean;
	branding?: (enabled: boolean) => IBrandingState;
	className?: string;
	headerLabel?: string;
	imagesLabel?: string;
	impersonationContext?: ImpersonationContextViewModel;
	/** Locks the media while the text stays editable, e.g. when the media is derived from elsewhere. */
	mediaReadOnly?: boolean;
	messageLabel?: string;
	onPostScheduled?: (postViewModel: SocialMediaPostViewModel) => void;
	post?: SocialMediaPostViewModel;
	readOnly?: boolean;
	styles?: StyleDeclarationValue[];
}

interface IFacebookMentionTrigger {
	end: number;
	query: string;
	start: number;
}

interface IAnchorPosition {
	left: number;
	top: number;
}

interface IHoveredMention {
	left: number;
	page: Api.IFacebookPageLookupResult;
	top: number;
}

const FACEBOOK_MENTION_TRIGGER_REGEX = /(^|\s)@(\S{0,2048})$/;
const FACEBOOK_VERIFICATION_BADGES = {
	blue_verified: { color: 'blue', label: 'Verified' },
	gray_verified: { color: 'gray', label: 'Verified' },
} as const;

// Rendered outside any TooltipProvider by the admin template editors, so the hint is a native one.
const CaptionCounter: React.FC<{ count: number; limit: number; prefix: string; tooltip: string }> = ({
	count,
	limit,
	prefix,
	tooltip,
}) => (
	<span className={css(styleSheet.captionCount, count > limit && styleSheet.captionCountOverLimit)} title={tooltip}>
		{`${prefix} ${count}/${limit}`}
	</span>
);

const FacebookVerificationStatusBadge: React.FC<{ verificationStatus?: string }> = ({ verificationStatus }) => {
	const normalizedStatus = verificationStatus?.trim().toLowerCase();
	const badge = normalizedStatus
		? FACEBOOK_VERIFICATION_BADGES[normalizedStatus as keyof typeof FACEBOOK_VERIFICATION_BADGES]
		: undefined;

	return badge ? <Pill color={badge.color} label={badge.label} size='small' variant='label' /> : null;
};

function getFacebookPageMetaLabel(page: Api.IFacebookPageLookupResult) {
	const handleLabel = page.handle ? `@${page.handle}` : '';
	return [handleLabel, getFacebookPageLocationLabel(page.location)].filter(Boolean).join(' · ');
}

function getFacebookMentionTrigger(value: string, caretIndex: number): IFacebookMentionTrigger | null {
	const textBeforeCaret = value.slice(0, caretIndex);
	const triggerMatch = textBeforeCaret.match(FACEBOOK_MENTION_TRIGGER_REGEX);
	if (!triggerMatch) {
		return null;
	}

	const query = triggerMatch[2] || '';
	return {
		end: caretIndex,
		query,
		start: textBeforeCaret.length - query.length - 1,
	};
}

export const MENTION_HINTS = {
	customizeForInstagram: 'Customize by platform to tag Instagram accounts.',
	facebook: 'Type @ to mention a Facebook Page.',
	instagram: 'Type @ and a handle to tag an Instagram account.',
	linkedIn: 'LinkedIn posts cannot mention accounts.',
} as const;

interface IMentionHintOptions {
	activePlatform?: Api.SocialMediaType;
	canCustomize: boolean;
	canInsertFacebookMentions: boolean;
	isInstagramTargeted: boolean;
	isLinkedInTargeted: boolean;
	isSplit: boolean;
}

function getMentionHint(options: IMentionHintOptions): string | null {
	const { activePlatform, canCustomize, canInsertFacebookMentions, isInstagramTargeted, isLinkedInTargeted } = options;
	if (options.isSplit) {
		switch (activePlatform) {
			case Api.SocialMediaType.Facebook:
				return canInsertFacebookMentions ? MENTION_HINTS.facebook : null;
			case Api.SocialMediaType.Instagram:
				return MENTION_HINTS.instagram;
			case Api.SocialMediaType.LinkedIn:
				return MENTION_HINTS.linkedIn;
			default:
				return null;
		}
	}

	const sentences: string[] = [];
	if (canInsertFacebookMentions) {
		sentences.push(MENTION_HINTS.facebook);
		const droppingPlatforms = [isInstagramTargeted && 'Instagram', isLinkedInTargeted && 'LinkedIn'].filter(Boolean);
		if (droppingPlatforms.length > 0) {
			sentences.push(`${droppingPlatforms.join(' and ')} will show only the name, untagged.`);
		}
	}
	if (canCustomize && isInstagramTargeted) {
		sentences.push(MENTION_HINTS.customizeForInstagram);
	}
	return sentences.length > 0 ? sentences.join(' ') : null;
}

const TEXT_METRIC_PROPERTIES = [
	'borderBottomWidth',
	'borderLeftWidth',
	'borderRightWidth',
	'borderTopWidth',
	'boxSizing',
	'fontFamily',
	'fontSize',
	'fontStyle',
	'fontVariant',
	'fontWeight',
	'letterSpacing',
	'lineHeight',
	'paddingBottom',
	'paddingLeft',
	'paddingRight',
	'paddingTop',
	'textAlign',
	'textIndent',
	'whiteSpace',
	'wordBreak',
	'wordSpacing',
	'wordWrap',
] as const;

function getTextareaAnchorPosition(textArea: HTMLTextAreaElement): IAnchorPosition {
	const computedStyle = window.getComputedStyle(textArea);
	const mirror = document.createElement('div');
	const marker = document.createElement('span');
	const textBeforeCaret = textArea.value.slice(0, textArea.selectionStart || 0);
	const textAfterCaret = textArea.value.slice(textArea.selectionStart || 0);

	mirror.style.position = 'absolute';
	mirror.style.visibility = 'hidden';
	mirror.style.whiteSpace = 'pre-wrap';
	mirror.style.wordWrap = 'break-word';
	mirror.style.overflow = 'hidden';
	mirror.style.width = computedStyle.width;

	TEXT_METRIC_PROPERTIES.forEach(property => {
		mirror.style[property] = computedStyle[property];
	});

	mirror.textContent = textBeforeCaret;
	marker.textContent = textAfterCaret || '.';
	mirror.appendChild(marker);
	textArea.ownerDocument.body.appendChild(mirror);

	const anchorPosition = {
		left: Math.max(12, marker.offsetLeft - textArea.scrollLeft),
		top: Math.max(12, marker.offsetTop - textArea.scrollTop + marker.offsetHeight),
	};

	textArea.ownerDocument.body.removeChild(mirror);
	return anchorPosition;
}

export const SocialMediaPostContentEditor: React.FC<IProps> = props => {
	const {
		allowPlatformCustomization,
		branding: getBrandingState,
		className,
		headerLabel,
		imagesLabel,
		impersonationContext,
		mediaReadOnly,
		messageLabel,
		onPostScheduled,
		post,
		readOnly,
		styles = [],
	} = props;
	const isMediaReadOnly = readOnly || mediaReadOnly;
	const editContext = useEditSocialMediaPostContext();
	const { facebookMentions, postContent, postTargets, selectedCreator, setContent, setFacebookMentions } = editContext;

	const userSession = useUserSession();
	const account = impersonationContext?.account || userSession.account;
	const platform = usePlatformCustomization({
		context: editContext,
		enabled: !!allowPlatformCustomization,
		impersonationContext: impersonationContext?.toJs(),
	});

	const editorContent = platform.isSplit ? platform.activeContent : (postContent ?? '');
	const editorMedia = platform.activeAttachments;
	const editorHasVideo = editorMedia.some(attachment => attachment.mimeType?.toLocaleLowerCase().startsWith('video/'));
	const setEditorContent = (content: string) =>
		platform.isSplit ? platform.setActiveContent(content) : setContent(content);

	const isInstagramTargeted = Boolean(postTargets?.some(target => target.provider === Api.SocialMediaType.Instagram));
	const isLinkedInTargeted = Boolean(postTargets?.some(target => target.provider === Api.SocialMediaType.LinkedIn));
	// While split is on, only the version that publishes to a platform carries that platform's rules.
	const isAuthoringFor = (provider: Api.SocialMediaType, isTargeted: boolean) =>
		platform.isSplit ? platform.activePlatform === provider : isTargeted;
	const showInstagramCaptionCounts = isAuthoringFor(Api.SocialMediaType.Instagram, isInstagramTargeted);
	// The post stores one set of mentions and they only ever publish to Facebook, so they index the
	// body Facebook receives: the Facebook version while split is on, the base post otherwise. That
	// body is the only one whose edits may move them, and the only one that may show them.
	const isFacebookBody = platform.isSplit ? platform.activePlatform === Api.SocialMediaType.Facebook : true;
	const showsFacebookMentions = isFacebookBody && isFacebookTargeted(postTargets);
	const canInsertFacebookMentions = Boolean(
		account?.features?.socialMedia?.enabled && showsFacebookMentions && !readOnly
	);
	const { data: brandingSettings } = useBrandingSettingsQuery({ impersonationContext: impersonationContext?.toJs() });
	// Any branding at all — colors, font, images, a brand description — is worth offering the action
	// for, because it applies the whole config rather than only the uploaded images.
	const brandingVersions = getPostBrandingVersions(editContext);
	const postWideMedia = brandingVersions.flatMap(version => [...version.attachments]);
	const canApplyBranding =
		!isMediaReadOnly && !getRebrandBlockedReason(postWideMedia, hasBrandingSettings(brandingSettings));
	// Matches EditSocialMediaPost: the run is scoped to whoever the post is sent from, not to the
	// impersonated session's own user, or the conversation and its attachments land on the wrong one.
	const brandingImpersonationContext = React.useMemo(
		() => Api.ImpersonationBroker.withForUser(impersonationContext?.toJs(), selectedCreator?.id),
		[impersonationContext, selectedCreator?.id]
	);
	const ownBranding = useBrandPostMedia({
		impersonationContext: brandingImpersonationContext,
		replaceMedia: (replacement, forPlatform) =>
			editContext.setActiveImages(current => replacement(current), forPlatform),
		templateId: editContext.template?.id,
		versions: brandingVersions,
	});
	const branding = (getBrandingState ?? ownBranding.getBrandingState)(canApplyBranding);
	useBrandingTabIndicator(getBrandingState ? 'off' : ownBranding.tabActivity);

	const inputRef = React.useRef<HTMLTextAreaElement>(null);
	const textAreaWrapperRef = React.useRef<HTMLDivElement>(null);
	const mentionHighlightRef = React.useRef<HTMLDivElement>(null);
	const [facebookMentionTrigger, setFacebookMentionTrigger] = React.useState<IFacebookMentionTrigger | null>(null);
	const [facebookMentionAnchorPosition, setFacebookMentionAnchorPosition] = React.useState<IAnchorPosition>({
		left: 14,
		top: 36,
	});
	const [hoveredMention, setHoveredMention] = React.useState<IHoveredMention | null>(null);
	const hoveredMentionHideTimeoutRef = React.useRef<number | null>(null);

	const facebookPageAutoComplete = useFacebookPageAutoCompleteViewModel({
		enabled: canInsertFacebookMentions && !!selectedCreator?.id,
		forUserId: selectedCreator?.id,
		impersonationContext: impersonationContext?.toJs(),
	});

	const mentionSegments = React.useMemo(
		() =>
			getMentionSegments(
				editorContent,
				showsFacebookMentions ? getFacebookMentionRanges(editorContent, facebookMentions) : []
			),
		[editorContent, facebookMentions, showsFacebookMentions]
	);
	const hasMentionHighlights = mentionSegments.some(segment => segment.isMention);
	// One body reaches every platform, so a Page mention lands on the networks that cannot link it and
	// they publish the name on its own. A split version publishes to one platform, so nothing leaks.
	const hasUnlinkedMentions = hasMentionHighlights && !platform.isSplit && (isInstagramTargeted || isLinkedInTargeted);

	const instagramCaptionCounts = React.useMemo(() => getInstagramCaptionCounts(editorContent), [editorContent]);
	const isInstagramVersion = platform.isSplit && platform.activePlatform === Api.SocialMediaType.Instagram;
	const instagramHandles = isInstagramVersion ? getInstagramHandles(editorContent, facebookMentions) : [];
	// The hint teaches how to mention; once the body holds one, the warnings below take over.
	const hasMentions =
		hasMentionHighlights ||
		instagramHandles.length > 0 ||
		(isInstagramVersion && hasInstagramPageMentions(editorContent, facebookMentions));
	const mentionHint =
		readOnly || hasMentions
			? null
			: getMentionHint({
					activePlatform: platform.activePlatform,
					canCustomize: platform.canCustomize,
					canInsertFacebookMentions,
					isInstagramTargeted,
					isLinkedInTargeted,
					isSplit: platform.isSplit,
				});

	const onInputRef = (ref: HTMLTextAreaElement) => {
		inputRef.current = ref;
	};

	const syncMentionHighlightScroll = React.useCallback(() => {
		if (!mentionHighlightRef.current || !inputRef.current) {
			return;
		}

		mentionHighlightRef.current.scrollTop = inputRef.current.scrollTop;
		mentionHighlightRef.current.scrollLeft = inputRef.current.scrollLeft;
	}, []);

	const cancelHoveredMentionHide = React.useCallback(() => {
		if (hoveredMentionHideTimeoutRef.current === null) {
			return;
		}

		window.clearTimeout(hoveredMentionHideTimeoutRef.current);
		hoveredMentionHideTimeoutRef.current = null;
	}, []);

	// Held open briefly so the pointer can travel from the mention into the bubble's own link.
	const hideHoveredMention = React.useCallback(() => {
		cancelHoveredMentionHide();
		hoveredMentionHideTimeoutRef.current = window.setTimeout(() => setHoveredMention(null), 200);
	}, [cancelHoveredMentionHide]);

	// Hit-test the non-interactive highlight layer through the textarea.
	const onTextAreaMouseMove = React.useCallback(
		(e: React.MouseEvent<HTMLTextAreaElement>) => {
			const highlightLayer = mentionHighlightRef.current;
			const wrapper = textAreaWrapperRef.current;
			if (!highlightLayer || !wrapper) {
				return;
			}

			const wrapperRect = wrapper.getBoundingClientRect();
			const hovered = Array.from(highlightLayer.querySelectorAll<HTMLElement>('[data-mention-segment]'))
				.flatMap(element => Array.from(element.getClientRects()).map(rect => ({ element, rect })))
				.find(
					({ rect }) =>
						e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom
				);

			if (!hovered) {
				hideHoveredMention();
				return;
			}

			const segment = mentionSegments[Number(hovered.element.dataset.mentionSegment)];
			if (!segment?.isMention) {
				hideHoveredMention();
				return;
			}

			cancelHoveredMentionHide();
			setHoveredMention({
				left: hovered.rect.left - wrapperRect.left,
				page: segment.page,
				top: hovered.rect.bottom - wrapperRect.top + 6,
			});
		},
		[cancelHoveredMentionHide, hideHoveredMention, mentionSegments]
	);

	const syncFacebookMentionTrigger = React.useCallback(
		(content: string, selectionStart?: number | null) => {
			if (!canInsertFacebookMentions || selectionStart === null || selectionStart === undefined || !inputRef.current) {
				setFacebookMentionTrigger(null);
				return;
			}

			const nextTrigger = getFacebookMentionTrigger(content, selectionStart);
			if (!nextTrigger) {
				setFacebookMentionTrigger(null);
				return;
			}

			setFacebookMentionTrigger(nextTrigger);
			setFacebookMentionAnchorPosition(getTextareaAnchorPosition(inputRef.current));
		},
		[canInsertFacebookMentions]
	);

	const onTextInputChanged = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const nextContent = e.target.value;
		setEditorContent(nextContent);
		syncFacebookMentionTrigger(nextContent, e.target.selectionStart);
	};

	const onTextAreaSelectionChanged = React.useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement> | React.MouseEvent<HTMLTextAreaElement>) => {
			if (!facebookMentionTrigger) {
				return;
			}

			const target = e.target as HTMLTextAreaElement;
			syncFacebookMentionTrigger(target.value, target.selectionStart);
		},
		[facebookMentionTrigger, syncFacebookMentionTrigger]
	);

	const onEmojiClick = (_: React.MouseEvent<Element, MouseEvent>, data: IEmojiData) => {
		if (!inputRef.current) {
			return;
		}

		const previousContent = editorContent;
		const selectionStart = inputRef.current.selectionStart || 0;
		const nextContent =
			previousContent.substring(0, selectionStart) +
			data.emoji +
			previousContent.substring(inputRef.current.selectionStart);
		const nextSelectionStart = selectionStart + data.emoji.length;

		setEditorContent(nextContent);

		window.requestAnimationFrame(() => {
			inputRef.current?.focus();
			inputRef.current?.setSelectionRange(nextSelectionStart, nextSelectionStart);
			syncFacebookMentionTrigger(nextContent, nextSelectionStart);
		});
	};

	// The mention goes in as plain "@Page Name" and the Page is remembered by id. The token form is
	// applied to whichever body is saved, so an edit anywhere in the post cannot break it.
	const onFacebookPageSelected = (page: Api.IFacebookPageLookupResult) => {
		if (!facebookMentionTrigger) {
			return;
		}

		const content = editorContent;
		const start = Math.max(0, Math.min(facebookMentionTrigger.start, content.length));
		const end = Math.max(start, Math.min(facebookMentionTrigger.end, content.length));
		const mentionText = `@${page.pageName || ''}`;
		const nextSelectionStart = start + mentionText.length;

		setEditorContent(content.slice(0, start) + mentionText + content.slice(end));
		setFacebookMentions(currentMentions =>
			(currentMentions || []).some(mention => mention.pageId === page.pageId)
				? currentMentions
				: [...(currentMentions || []), page]
		);
		setFacebookMentionTrigger(null);
		facebookPageAutoComplete.reset();

		window.requestAnimationFrame(() => {
			inputRef.current?.focus();
			inputRef.current?.setSelectionRange(nextSelectionStart, nextSelectionStart);
		});
	};

	const onValidateSelectedFile = React.useCallback(
		(file: File) => {
			const mimeType = file.type?.toLocaleLowerCase();
			if (!(mimeType.startsWith('video/') || mimeType.startsWith('image/'))) {
				toast.error('Please select a valid image or video file.');
				return false;
			}

			if (mimeType.startsWith('video/') && !editorHasVideo && editorMedia.length > 0) {
				toast.error('This post already has a image. Please remove the image to add a video.');
				return false;
			}

			if (mimeType.startsWith('image/') && editorHasVideo) {
				toast.error('This post already has a video. Please remove the video to add an image.');
				return false;
			}

			return true;
		},
		[editorHasVideo, editorMedia.length]
	);

	React.useEffect(() => {
		if (canInsertFacebookMentions) {
			return;
		}

		setFacebookMentionTrigger(null);
	}, [canInsertFacebookMentions]);

	React.useEffect(() => cancelHoveredMentionHide, [cancelHoveredMentionHide]);

	const showPlatformToggle = platform.canCustomize && !readOnly;
	const visibleValidationErrors = platform.isSplit ? platform.activeErrors : platform.errors;
	// Undefined until GET social/platformCapabilities answers. Nothing is over a limit we do not know.
	const characterLimit = platform.characterLimit;
	const isOverCharacterLimit = typeof characterLimit === 'number' && editorContent.length > characterLimit;
	const mentionDropdownSearchQuery = facebookMentionTrigger ? facebookMentionTrigger.query || ' ' : '';
	const normalizedMentionSearch = facebookMentionTrigger?.query?.trim() || '';
	// A Page loaded back from a saved post carries only its id and name; the id alone still links to it.
	const hoveredMentionLink = hoveredMention?.page.link || `https://www.facebook.com/${hoveredMention?.page.pageId}`;
	const showBrandingAction = canApplyBranding || branding.isBranding;
	const showVersionBar = platform.canCustomize || showBrandingAction;
	const renderBrandingStatus = () => {
		if (branding.isBranding) {
			return (
				<span aria-live='polite' className={css(styleSheet.brandingStatus)} role='status'>
					{branding.progressText}
				</span>
			);
		}

		if (branding.hasFailed) {
			return (
				<span className={css(styleSheet.brandingStatus, styleSheet.brandingStatusFailed)}>
					Branding didn’t finish. Try again.
				</span>
			);
		}

		if (branding.failedImages > 0) {
			return (
				<span className={css(styleSheet.brandingStatus, styleSheet.brandingStatusFailed)}>
					{branding.failedImages === 1
						? '1 image couldn’t be branded'
						: `${branding.failedImages} images couldn’t be branded`}
				</span>
			);
		}

		return branding.allImagesBranded ? (
			<span className={css(styleSheet.brandingStatus)}>All images branded</span>
		) : null;
	};
	// The status trails the controls so the run's text — which renames itself on every image — grows
	// into the empty strip to their right instead of walking them along it.
	const renderBrandingAction = () => (
		<div className={css(styleSheet.brandingAction)}>
			<div className={css(styleSheet.brandingActionButtons)}>
				<AiButton
					disabled={!branding.canApply}
					isLoading={branding.isBranding}
					onClick={branding.apply}
					size='sm'
					title='Apply my Branding'
				/>
				{branding.isBranding ? (
					<Button onClick={branding.cancel} size='sm' variant='secondary'>
						Cancel
					</Button>
				) : branding.canUndo ? (
					<Button onClick={branding.undo} size='sm' variant='secondary'>
						Undo branding
					</Button>
				) : null}
			</div>
			{renderBrandingStatus()}
		</div>
	);

	return (
		<div className={`${css(styleSheet.container, ...styles)} social-media-post-content-editor ${className || ''}`}>
			<div className={css(styleSheet.headerContainer)}>
				{headerLabel && !readOnly && <div className={css(styleSheet.header)}>{headerLabel}</div>}
			</div>
			{showVersionBar ? (
				<PlatformVersionBar
					actions={showBrandingAction ? renderBrandingAction() : null}
					activePlatform={platform.activePlatform}
					discardedChanges={platform.discardedChanges}
					discardSpreadsMedia={platform.discardSpreadsMedia}
					isPlatformEdited={platform.isPlatformEdited}
					isSplit={platform.isSplit}
					limits={
						platform.canCustomize ? (
							<PlatformLimits
								activePlatform={platform.activePlatform}
								capabilities={platform.capabilities}
								isSplit={platform.isSplit}
							/>
						) : null
					}
					onResetToBase={platform.resetActiveToBase}
					onSelectPlatform={platform.setActivePlatform}
					onToggleSplit={showPlatformToggle ? platform.setCustomizeByPlatform : undefined}
					panelId={VERSION_PANEL_ID}
					platformHasErrors={platform.platformHasErrors}
					platformHasHandlesToConfirm={platform.platformHasHandlesToConfirm}
					platforms={platform.canCustomize ? platform.targetedPlatforms : []}
					readOnly={readOnly}
				/>
			) : null}
			<div
				aria-label={platform.isSplit ? `${platform.activePlatform} version` : undefined}
				className={css(styleSheet.versionPanel, platform.isSplit && styleSheet.versionPanelSplit)}
				id={VERSION_PANEL_ID}
				role={platform.isSplit ? 'tabpanel' : undefined}
			>
				<div>
					<div className={css(styleSheet.composerLabelRow)}>
						<div className={`emoji-label-container ${css(styleSheet.labelEmoji)}`}>
							{messageLabel ? <label htmlFor={CONTENT_TEXT_AREA_ID}>{messageLabel}</label> : null}
						</div>
						<div className={css(styleSheet.composerMeters)}>
							<EmojiPickerButton onEmojiClick={!readOnly ? onEmojiClick : null} popoverPlacement={['left', 'top']} />
							{showInstagramCaptionCounts ? (
								<CaptionCounter
									count={instagramCaptionCounts.hashtags}
									limit={INSTAGRAM_CAPTION_LIMITS.hashtags}
									prefix='#'
									tooltip={`Instagram allows ${INSTAGRAM_CAPTION_LIMITS.hashtags} hashtags per caption`}
								/>
							) : null}
							<span
								className={css(styleSheet.characterCount, isOverCharacterLimit && styleSheet.characterCountOverLimit)}
								title={
									typeof characterLimit === 'number'
										? `${
												platform.isSplit ? platform.activePlatform : 'This post'
											} allows ${characterLimit.toLocaleString()} characters`
										: undefined
								}
							>
								{typeof characterLimit === 'number'
									? `${editorContent.length.toLocaleString()}/${characterLimit.toLocaleString()}`
									: editorContent.length.toLocaleString()}
							</span>
						</div>
					</div>
					<div className={css(styleSheet.composerField, isOverCharacterLimit && styleSheet.composerFieldOverLimit)}>
						<PlatformValidationErrors errors={visibleValidationErrors} inField />
						<div className={css(styleSheet.textAreaWrapper)} ref={textAreaWrapperRef}>
							{hasMentionHighlights ? (
								<div
									aria-hidden='true'
									className={css(
										styleSheet.mentionHighlightLayer,
										readOnly && styleSheet.mentionHighlightLayerDisabled
									)}
									ref={mentionHighlightRef}
								>
									{mentionSegments.map((segment, index) =>
										segment.isMention ? (
											<span
												className={css(styleSheet.mentionHighlight)}
												data-mention-segment={index}
												data-testid='facebook-mention-highlight'
												key={`mention-${index}`}
											>
												{segment.text}
											</span>
										) : (
											<React.Fragment key={`text-${index}`}>{segment.text}</React.Fragment>
										)
									)}
									{'\n'}
								</div>
							) : null}
							<TextArea
								id={CONTENT_TEXT_AREA_ID}
								disabled={readOnly}
								styles={[
									styleSheet.textAreaContainer,
									...(hasMentionHighlights ? [styleSheet.textAreaOverHighlights] : []),
								]}
								onChange={onTextInputChanged}
								onClick={onTextAreaSelectionChanged}
								onKeyUp={onTextAreaSelectionChanged}
								onMouseLeave={hasMentionHighlights ? hideHoveredMention : undefined}
								onMouseMove={hasMentionHighlights ? onTextAreaMouseMove : undefined}
								onScroll={syncMentionHighlightScroll}
								ref={onInputRef}
								value={editorContent}
							/>
							{hoveredMention ? (
								<div
									className={css(styleSheet.mentionBubble)}
									onMouseEnter={cancelHoveredMentionHide}
									onMouseLeave={hideHoveredMention}
									style={{ left: `${hoveredMention.left}px`, top: `${hoveredMention.top}px` }}
								>
									<div className={css(styleSheet.mentionResultNameRow)}>
										<div className={css(styleSheet.mentionResultName)}>{hoveredMention.page.pageName}</div>
										<FacebookVerificationStatusBadge verificationStatus={hoveredMention.page.verificationStatus} />
									</div>
									{getFacebookPageMetaLabel(hoveredMention.page) ? (
										<div className={css(styleSheet.mentionResultMeta)}>
											{getFacebookPageMetaLabel(hoveredMention.page)}
										</div>
									) : null}
									<a
										className={css(styleSheet.mentionBubbleLink)}
										href={hoveredMentionLink}
										rel='noopener noreferrer'
										target='_blank'
										title={hoveredMentionLink}
									>
										View page
									</a>
								</div>
							) : null}
							{canInsertFacebookMentions && facebookMentionTrigger ? (
								<AutoCompleteDropdown<Api.IFacebookPageLookupResult, Api.IFacebookPageLookupResult>
									anchor={() => (
										<span
											aria-hidden='true'
											className={css(styleSheet.mentionAnchor)}
											style={{
												left: `${facebookMentionAnchorPosition.left}px`,
												top: `${facebookMentionAnchorPosition.top}px`,
											}}
										/>
									)}
									autoCompleteViewModel={facebookPageAutoComplete}
									contentPositionY='bottom'
									dropdownContentItemsStyles={[styleSheet.mentionDropdown]}
									isOpen={true}
									loaderType='small'
									onItemSelected={onFacebookPageSelected}
									onRenderEmptyResults={() => (
										<div className={css(styleSheet.mentionEmptyState)}>
											{normalizedMentionSearch
												? `No Facebook Pages found for @${normalizedMentionSearch}.`
												: 'Type a Facebook Page handle or paste a Facebook Page URL.'}
										</div>
									)}
									onRenderItem={(item, index, highlighted, onClick) => {
										const metaLabel = getFacebookPageMetaLabel(item);
										return (
											<div
												className={css(
													styleSheet.mentionResultRow,
													highlighted && styleSheet.mentionResultRowHighlighted
												)}
												key={`${item.pageId || item.handle || 'facebook-page'}-${index}`}
											>
												<button
													className={css(styleSheet.mentionResultItem)}
													onMouseDown={e => e.preventDefault()}
													type='button'
													onClick={() => onClick()}
												>
													<div className={css(styleSheet.mentionResultNameRow)}>
														<div className={css(styleSheet.mentionResultName)}>{item.pageName}</div>
														<FacebookVerificationStatusBadge verificationStatus={item.verificationStatus} />
													</div>
													{metaLabel ? <div className={css(styleSheet.mentionResultMeta)}>{metaLabel}</div> : null}
												</button>
												{item.link ? (
													<a
														className={css(styleSheet.mentionResultLink)}
														href={item.link}
														onMouseDown={e => e.stopPropagation()}
														rel='noopener noreferrer'
														target='_blank'
														title={item.link}
													>
														View page
													</a>
												) : null}
											</div>
										);
									}}
									searchQuery={mentionDropdownSearchQuery}
									sectionTitleElement={
										<div className={css(styleSheet.mentionSectionTitle)}>Select the Page you want to mention</div>
									}
								/>
							) : null}
						</div>
					</div>
					<InstagramHandlesWarning handles={instagramHandles} />
					{mentionHint ? <div className={css(styleSheet.mentionDisclaimer)}>{mentionHint}</div> : null}
					{hasUnlinkedMentions ? (
						<div className={css(styleSheet.mentionDisclaimer)}>
							Only Facebook supports Page mentions. Instagram and LinkedIn show the name on its own, without the @, and
							nobody is notified there.
							{showPlatformToggle && isInstagramTargeted ? ` ${MENTION_HINTS.customizeForInstagram}` : null}
						</div>
					) : null}
				</div>

				<div className={`add-media-container ${css(styleSheet.mediaContainer)}`}>
					<SocialMediaImageSearchAndDisplay
						attachments={editorMedia}
						generatingAttachmentIds={branding.brandingImageIds}
						label={imagesLabel && !isMediaReadOnly ? imagesLabel : 'Media'}
						readOnly={isMediaReadOnly}
						onValidateSelectedFile={onValidateSelectedFile}
						platform={platform.editedPlatform}
						publishedAttachments={platform.publishedAttachments}
						post={post}
						onPostScheduled={onPostScheduled}
					/>
				</div>
			</div>
		</div>
	);
};
